import { createReducer, on } from '@ngrx/store';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

import { Task } from '../../interface/task.interface';
import * as actions from '../actions';
import { tasksState } from '../states/tasks.state';

export const TASKS_REDUCER_KEY = 'tasks';

const collectLoadedTaskData = (tasks: Task[], dataMap: Map<string, { links: any; notifiers: any }>): void => {
    tasks?.forEach(task => {
        if (task.links !== undefined || task.notifiers !== undefined) {
            dataMap.set(task.id, { links: task.links, notifiers: task.notifiers });
        }
        if (task.childTasks?.length) {
            collectLoadedTaskData(task.childTasks, dataMap);
        }
    });
};

const restoreLoadedTaskData = (tasks: Task[], dataMap: Map<string, { links: any; notifiers: any }>): Task[] => {
    return tasks?.map(task => {
        const existing = dataMap.get(task.id);
        const restored = existing
            ? {
                  ...task,
                  ...(existing.links !== undefined && { links: existing.links }),
                  ...(existing.notifiers !== undefined && { notifiers: existing.notifiers }),
              }
            : task;
        return task.childTasks?.length ? { ...restored, childTasks: restoreLoadedTaskData(task.childTasks, dataMap) } : restored;
    });
};

const updateTaskWithChildrenRecursively = (tasks: Task[], parentTaskId: string, tasksToPush: Task[]): { tasks: Task[]; found: boolean } => {
    let found = false;
    const updatedTasks = tasks?.map(task => {
        if (task.id === parentTaskId) {
            found = true;
            if (!tasksToPush?.length) {
                return { ...task, childTasks: [] };
            }
            const taskIds = tasksToPush.map(t => t.id);
            const existingDataMap = new Map<string, { links: any; notifiers: any }>();
            collectLoadedTaskData(task.childTasks, existingDataMap);
            const mergedTasksToPush = restoreLoadedTaskData(tasksToPush, existingDataMap);
            return { ...task, childTasks: [...(task.childTasks?.filter(t => !taskIds.includes(t.id)) || []), ...mergedTasksToPush] };
        }
        if (task.childTasks?.length) {
            const { tasks: updatedChildren, found: foundInChildren } = updateTaskWithChildrenRecursively(
                task.childTasks,
                parentTaskId,
                tasksToPush
            );
            if (foundInChildren) {
                found = true;
                return { ...task, childTasks: updatedChildren };
            }
        }
        return task;
    });
    return { tasks: updatedTasks, found };
};

const deleteTaskWithChildrenRecursively = (tasks: Task[], tasksToDelete: Task[]): { tasks: Task[]; found: boolean } => {
    let found = false;
    const updatedTasks = tasks
        ?.map(task => {
            if (tasksToDelete.some(t => t.id === task.id)) {
                found = true;
                return null;
            }
            if (task.childTasks?.length) {
                const { tasks: updatedChildren, found: foundInChildren } = deleteTaskWithChildrenRecursively(
                    task.childTasks,
                    tasksToDelete
                );
                if (foundInChildren) found = true;
                return { ...task, childTasks: updatedChildren };
            }
            return task;
        })
        .filter(Boolean) as Task[];
    return { tasks: updatedTasks, found };
};

const updateOrgTasks = (state: any, updateFn: (tasks: Task[]) => Task[]): any => {
    const orgId = state.currentOrganizationId;
    if (!orgId) return state;

    const orgData = state.organizationsMap[orgId] || { tasks: [], taskAssignees: {}, taskInstances: {}, comments: {} };
    const tasks = CommonUtilsService.cloneObject(orgData.tasks || []);

    return {
        ...state,
        organizationsMap: {
            ...state.organizationsMap,
            [orgId]: {
                ...orgData,
                tasks: updateFn(tasks),
            },
        },
    };
};

const updateTaskRecursively = (
    tasks: Task[],
    taskToUpdate: Task,
    { id, assignments }: { id?: string; assignments?: Task['assignments'] }
): Task[] => {
    return tasks?.map(task => {
        if (task.id === id) {
            return { ...task, assignments };
        } else if (task.id === taskToUpdate?.id) {
            return {
                ...task,
                ...taskToUpdate,
                childTasks: (task.childTasks?.length > 0 ? task.childTasks : undefined) || taskToUpdate?.childTasks || [],
            };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: updateTaskRecursively(task.childTasks, taskToUpdate, { id, assignments }),
            };
        }
        return task;
    });
};

const updateTaskLinksRecursively = (tasks: Task[], taskId: string, links: any[]): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            return { ...task, links: CommonUtilsService.cloneObject(links) };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: updateTaskLinksRecursively(task.childTasks, taskId, links),
            };
        }
        return task;
    });
};

const createTaskLinkRecursively = (tasks: Task[], taskId: string, link: any): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            const links = task.links || [];
            const index = links.findIndex(l => l.id === link.id);
            const updatedLinks = index > -1 ? links.map((l, i) => (i === index ? link : l)) : [...links, link];
            return { ...task, links: updatedLinks };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: createTaskLinkRecursively(task.childTasks, taskId, link),
            };
        }
        return task;
    });
};

const deleteTaskLinkRecursively = (tasks: Task[], taskId: string, linkId: string): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            return { ...task, links: task.links?.filter(link => link.id !== linkId) || [] };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: deleteTaskLinkRecursively(task.childTasks, taskId, linkId),
            };
        }
        return task;
    });
};

const updateTaskNotifiersRecursively = (tasks: Task[], taskId: string, notifiers: any[]): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            return { ...task, notifiers: CommonUtilsService.cloneObject(notifiers) };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: updateTaskNotifiersRecursively(task.childTasks, taskId, notifiers),
            };
        }
        return task;
    });
};

const createTaskNotifierRecursively = (tasks: Task[], taskId: string, notifier: any): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            const notifiers = task.notifiers || [];
            const index = notifiers.findIndex(n => n.event_type === notifier.event_type);
            const updatedNotifiers = index > -1 ? notifiers.map((n, i) => (i === index ? notifier : n)) : [...notifiers, notifier];
            return { ...task, notifiers: updatedNotifiers };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: createTaskNotifierRecursively(task.childTasks, taskId, notifier),
            };
        }
        return task;
    });
};

const deleteTaskNotifierRecursively = (tasks: Task[], taskId: string, eventType: string): Task[] => {
    return tasks?.map(task => {
        if (task.id === taskId) {
            return { ...task, notifiers: task.notifiers?.filter(notifier => notifier.event_type !== eventType) || [] };
        }
        if (task.childTasks?.length) {
            return {
                ...task,
                childTasks: deleteTaskNotifierRecursively(task.childTasks, taskId, eventType),
            };
        }
        return task;
    });
};

export const TASKS_REDUCER = createReducer(
    tasksState,
    on(actions.SetCurrentOrganization, (state, action) => {
        return {
            ...state,
            currentOrganizationId: action.organization?.id,
        };
    }),
    on(actions.CreateLinkSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => createTaskLinkRecursively(tasks, state.selectedTaskId, action.link));
    }),

    on(actions.DeleteLinkSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => deleteTaskLinkRecursively(tasks, state.selectedTaskId, action.linkId));
    }),

    on(actions.GetLinksSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => updateTaskLinksRecursively(tasks, state.selectedTaskId, action.links));
    }),

    on(actions.CreateNotifierSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => createTaskNotifierRecursively(tasks, state.selectedTaskId, action.notifier));
    }),

    on(actions.UpdateNotifierSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => createTaskNotifierRecursively(tasks, state.selectedTaskId, action.notifier));
    }),

    on(actions.DeleteNotifierSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => deleteTaskNotifierRecursively(tasks, state.selectedTaskId, action.eventType));
    }),

    on(actions.GetNotifiersSuccess, (state, action) => {
        return updateOrgTasks(state, tasks => updateTaskNotifiersRecursively(tasks, state.selectedTaskId, action.notifiers));
    }),

    on(actions.SetTaskDefinitionId, (state, action) => {
        return {
            ...state,
            taskDefinitionId: action.taskId,
        };
    }),
    on(actions.SetSelectedTaskType, (state, action) => {
        return {
            ...state,
            selectedTaskType: action.taskType,
        };
    }),
    on(actions.SetSelectedTaskId, (state, action) => {
        return {
            ...state,
            selectedTaskId: action.taskId,
        };
    }),
    on(actions.SetTasks, (state, action) => {
        if (state?.currentOrganizationId?.length === 0) {
            return state;
        }
        let tasks: Task[] = CommonUtilsService.cloneObject(action.tasks);
        const currentTasks = CommonUtilsService.cloneObject(state?.organizationsMap?.[state.currentOrganizationId]?.tasks || []);
        tasks?.forEach(task => {
            task.childTasks = task.childTasks || [];
        });
        if (currentTasks?.length > 0) {
            currentTasks.forEach((task: Task) => {
                tasks = updateTaskRecursively(tasks, task, {});
            });
        }
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    tasks: tasks,
                },
            },
        };
    }),
    on(actions.SetChildTasks, (state, action) => {
        if (state?.currentOrganizationId?.length === 0) {
            return state;
        }
        const tasks = CommonUtilsService.cloneObject(state?.organizationsMap?.[state.currentOrganizationId]?.tasks);
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    tasks: tasks ? updateTaskWithChildrenRecursively(tasks, action.parentTaskId, action.tasks)?.tasks : action.tasks,
                },
            },
        };
    }),
    on(actions.SetTaskInstances, (state, action) => {
        if (state?.currentOrganizationId?.length === 0 || state?.taskDefinitionId?.length === 0) {
            return state;
        }
        let taskInstances: Task[] = CommonUtilsService.cloneObject(action.taskInstances);
        const currentTasks = state?.organizationsMap?.[state.currentOrganizationId]?.taskInstances?.[state.taskDefinitionId] || [];
        taskInstances.forEach(task => {
            task.childTasks = task.childTasks || [];
        });
        if (currentTasks?.length > 0) {
            taskInstances.forEach(task => {
                taskInstances = updateTaskRecursively(currentTasks, task, {});
            });
        }
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskInstances: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskInstances,
                        [state.taskDefinitionId]: taskInstances,
                    },
                },
            },
        };
    }),
    on(actions.SetChildTaskInstances, (state, action) => {
        if (state?.currentOrganizationId?.length === 0 || state?.taskDefinitionId?.length === 0) {
            return state;
        }
        const tasks: Task[] = CommonUtilsService.cloneObject(
            state?.organizationsMap?.[state.currentOrganizationId]?.taskInstances?.[state.taskDefinitionId] || []
        );
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskInstances: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskInstances,
                        [state.taskDefinitionId]: updateTaskWithChildrenRecursively(tasks, action.parentTaskId, action.tasks)?.tasks,
                    },
                },
            },
        };
    }),
    on(actions.SetFilteredChildTaskInstances, (state, action) => ({
        ...state,
        organizationsMap: {
            ...state.organizationsMap,
            [state.currentOrganizationId]: {
                ...state.organizationsMap?.[state.currentOrganizationId],
                filteredChildTaskInstanceIds: {
                    ...state.organizationsMap?.[state.currentOrganizationId]?.filteredChildTaskInstanceIds,
                    [action.rootInstanceId]: action.taskIds,
                },
            },
        },
    })),
    on(actions.ClearFilteredChildTaskInstances, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    filteredChildTaskInstanceIds: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.filteredChildTaskInstanceIds,
                        [action.rootInstanceId]: undefined,
                    },
                },
            },
        };
    }),
    on(actions.SetTaskInstance, (state, action) => {
        if (state?.currentOrganizationId?.length === 0 || state?.taskDefinitionId?.length === 0) {
            return state;
        }
        let tasks: Task[] = CommonUtilsService.cloneObject(
            state?.organizationsMap?.[state.currentOrganizationId]?.taskInstances?.[state.taskDefinitionId] || []
        );
        switch (action.actionType) {
            case 'CREATE':
                if (action.task.parent === '00000000-0000-0000-0000-000000000000') {
                    tasks.unshift(action.task);
                } else {
                    tasks = updateTaskWithChildrenRecursively(tasks, action.task.parent, [action.task])?.tasks;
                }
                break;
            case 'EDIT':
                if (tasks.length > 0) {
                    tasks = updateTaskRecursively(tasks, action.task, {});
                } else if (action.task.parent === '00000000-0000-0000-0000-000000000000') {
                    tasks.unshift(action.task);
                } else {
                    tasks.push(action.task);
                }
                break;
            case 'DELETE':
                tasks = deleteTaskWithChildrenRecursively(tasks, [action.task])?.tasks;
                break;
        }
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskInstances: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskInstances,
                        [state.taskDefinitionId]: tasks,
                    },
                },
            },
        };
    }),
    on(actions.SetTask, (state, action) => {
        let tasks: Task[] = CommonUtilsService.cloneObject(state?.organizationsMap?.[state.currentOrganizationId]?.tasks || []);
        switch (action.actionType) {
            case 'CREATE':
                if (action.task.parent === '00000000-0000-0000-0000-000000000000') {
                    tasks.unshift(action.task);
                } else {
                    tasks = updateTaskWithChildrenRecursively(tasks, action.task.parent, [action.task])?.tasks;
                }
                break;
            case 'EDIT':
                if (tasks?.length > 0) {
                    tasks = updateTaskRecursively(tasks, action.task, {});
                } else {
                    tasks.unshift(action.task);
                }
                break;
            case 'DELETE':
                tasks = deleteTaskWithChildrenRecursively(tasks, [action.task])?.tasks;
                break;
        }
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    tasks: tasks,
                },
            },
        };
    }),

    on(actions.SetStatusGroups, (state, { statusGroups }) => ({
        ...state,
        statusGroups: statusGroups,
    })),

    on(actions.CreateStatusGroupSuccess, (state, { statusGroup }) => {
        return {
            ...state,
            statusGroups: [...state.statusGroups.filter(g => g.id !== statusGroup.id), statusGroup],
        };
    }),

    on(actions.SetTaskAssignee, (state, action) => {
        let taskInstances: Task[] = CommonUtilsService.cloneObject(
            state?.organizationsMap?.[state.currentOrganizationId]?.taskInstances?.[state.taskDefinitionId] || []
        );
        if (action.taskType === 'TASK_INSTANCE') {
            taskInstances = updateTaskRecursively(taskInstances, undefined, { id: action.taskId, assignments: action.assignees });
        }
        let taskDefinitions: Task[] = CommonUtilsService.cloneObject(state?.organizationsMap?.[state.currentOrganizationId]?.tasks || []);
        if (action.taskType === 'TASK_DEFINITION') {
            taskDefinitions = updateTaskRecursively(taskDefinitions, undefined, { id: action.taskId, assignments: action.assignees });
        }
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskAssignees: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskAssignees,
                        [action.taskId]: action.assignees,
                    },
                    taskInstances: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskInstances,
                        [state.taskDefinitionId]: taskInstances,
                    },
                    tasks: taskDefinitions,
                },
            },
        };
    }),
    on(actions.SetFutureTaskInstances, (state, action) => {
        return {
            ...state,
            futureTaskSchedules: action.futureSchedules,
        };
    }),
    on(actions.ClearFutureTaskInstances, state => ({
        ...state,
        futureTaskSchedules: [],
    })),
    on(actions.SetComments, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    comments: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.comments,
                        [action.taskId]: action.comments,
                    },
                },
            },
        };
    }),
    on(actions.SetComment, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    comments: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.comments,
                        [action.taskId]: [
                            ...(state.organizationsMap?.[state.currentOrganizationId]?.comments?.[action.taskId] || []),
                            action.comment,
                        ],
                    },
                },
            },
        };
    }),
    on(actions.SetActionUpdates, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    actionUpdates: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.actionUpdates,
                        [action.taskId]: action.actionUpdates,
                    },
                },
            },
        };
    }),
    on(actions.SetSelectedParentInstanceId, (state, action) => ({
        ...state,
        selectedRootInstanceId: action.instanceId,
    })),

    // Task Actions
    on(actions.SetTaskActions, (state, { actions: taskActions, taskDefinitionId }) => {
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskActions: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskActions,
                        [taskDefinitionId]: taskActions,
                    },
                },
            },
        };
    }),

    on(actions.SaveTaskActionSuccess, (state, { action: savedAction, taskDefinitionId }) => {
        const currentActions = state.organizationsMap?.[state.currentOrganizationId]?.taskActions?.[taskDefinitionId] || [];
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskActions: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskActions,
                        [taskDefinitionId]: [...currentActions, savedAction],
                    },
                },
            },
        };
    }),

    on(actions.UpdateTaskActionSuccess, (state, { action: updatedAction, taskDefinitionId }) => {
        const currentActions = state.organizationsMap?.[state.currentOrganizationId]?.taskActions?.[taskDefinitionId] || [];
        const updatedActions = currentActions.map(action => (action.id === updatedAction.id ? updatedAction : action));
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskActions: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskActions,
                        [taskDefinitionId]: updatedActions,
                    },
                },
            },
        };
    }),

    on(actions.DeleteTaskActionSuccess, (state, { actionId, taskDefinitionId }) => {
        const currentActions = state.organizationsMap?.[state.currentOrganizationId]?.taskActions?.[taskDefinitionId] || [];
        const filteredActions = currentActions.filter(action => action.id !== actionId);
        return {
            ...state,
            organizationsMap: {
                ...state.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state.organizationsMap?.[state.currentOrganizationId],
                    taskActions: {
                        ...state.organizationsMap?.[state.currentOrganizationId]?.taskActions,
                        [taskDefinitionId]: filteredActions,
                    },
                },
            },
        };
    }),

    on(actions.SessionLogoutSuccessfull, () => tasksState)
);
